import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { IconCheck } from '@tabler/icons-react'
import { notifications } from 'context'
import { StepperContext } from '../../../context/StepperContext'
import { SkillsStandardsContext } from '../../../context/SkillsStandardsContext'
import { applyAndRefetchAIItems } from '../../../utils/aiSuggestionsApplyHelper'

interface ProjectItem {
  id: string
  name: string
  original_codes?: string
  originalCodes?: string
  cycles?: Array<{ name: string }>
  breadcrumb?: string
  lv1_name?: string
  lv1Name?: string
  color?: string
  curriculum_kind?: number
  curriculumKind?: number
  lv1Id?: string
  lv2Id?: string
  lv2Name?: string
  lv3Id?: string
  lv3Name?: string
  curriculumId?: string
  curriculumName?: string
}

interface UseAISuggestionsModalProps {
  opened: boolean
  onClose: () => void
  projectId?: string | null
}

interface UseAISuggestionsModalReturn {
  loading: boolean
  projectItems: { contents: ProjectItem[], skills: ProjectItem[] }
  selectedIds: Set<string>
  activeTab: string
  totalItems: number
  hasItems: boolean
  availableItems: { contents: ProjectItem[], skills: ProjectItem[] }
  totalAvailableItems: number
  allItemsAlreadyAdded: boolean
  handleToggleItem: (itemId: string) => void
  handleSelectAll: (type: 'contents' | 'skills') => void
  handleApplySuggestions: () => Promise<void>
  setActiveTab: (tab: string) => void
}

function isValidItem(item: any): boolean {
  return item && typeof item === 'object' && item.id
}

function getValidItems(items: any[]): ProjectItem[] {
  if (!Array.isArray(items)) return []
  return items.filter(isValidItem)
}

function extractProjectItems(data: any): { contents: ProjectItem[], skills: ProjectItem[] } {
  const result = { contents: [] as ProjectItem[], skills: [] as ProjectItem[] }

  if (!data) {
    return result
  }

  const items = data.fullApiResponse?.projectItems

  if (!items || typeof items !== 'object') {
    return result
  }
  
  try {
    result.contents = getValidItems(items.contents)
    result.skills = getValidItems(items.specificSkills)
  } catch (error) {
  }

  return result
}

function safeProcessCycles(cycles: any): any[] {
  try {
    if (!cycles || !Array.isArray(cycles)) return []

    return cycles.map((cycle, index) => {
      try {
        if (cycle?.name && Array.isArray(cycle.name) && cycle.name[0]) {
          const firstCycle = cycle.name[0]
          return {
            id: firstCycle.id || `cycle-${index}`,
            name: firstCycle.name || ''
          }
        }
        if (cycle?.name && typeof cycle.name === 'string') {
          return {
            id: cycle.id || `cycle-${cycle.name}`,
            name: cycle.name
          }
        }
        if (typeof cycle === 'string') {
          return { id: `cycle-${cycle}`, name: cycle }
        }
        if (cycle?.id && cycle?.name && typeof cycle.name === 'string') {
          return { id: cycle.id, name: cycle.name }
        }
        return { id: `cycle-${index}`, name: 'Indefinido' }
      } catch (e) {
        return { id: `cycle-${index}`, name: 'Indefinido' }
      }
    }).filter(cycle => cycle.name !== 'Indefinido')
  } catch (error) {
    return []
  }
}

function safeProcessGrades(cycles: any): any[] {
  try {
    if (!cycles || !Array.isArray(cycles)) return []

    return cycles.map((cycle, index) => {
      try {
        let name = ''

        if (cycle?.name && Array.isArray(cycle.name) && cycle.name[0]) {
          name = cycle.name[0].name || ''
        } else if (cycle?.name && typeof cycle.name === 'string') {
          name = cycle.name
        } else if (typeof cycle === 'string') {
          name = cycle
        } else if (cycle?.name) {
          name = String(cycle.name)
        }

        return name
      } catch (e) {
        return ''
      }
    }).filter(name => name && name.length > 0)
  } catch (error) {
    return []
  }
}

export const useAISuggestionsModal = ({ opened, onClose, projectId }: UseAISuggestionsModalProps): UseAISuggestionsModalReturn => {
  const { t } = useTranslation()
  const { aiContext, projectId: contextProjectId } = useContext(StepperContext)
  const {
    handleAddAIItems,
    checkIfSelected,
    setSkillsStandards,
    selectedStandards,
    selectedSkills,
    aiSuggestedStandards,
    aiSuggestedSkills,
    forceReloadSelectedItems
  } = useContext(SkillsStandardsContext)

  const realProjectId = contextProjectId || projectId

  const [loading, setLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<string>('contents')
  
  const projectItems = useMemo(() => {
    try {
      const storageKey = `ai_generator_context_${realProjectId || 'new'}`
      const storedData = localStorage.getItem(storageKey)

      if (storedData) {
        try {
          const parsed = JSON.parse(storedData)
          if (parsed && typeof parsed === 'object') {
            let extracted = extractProjectItems(parsed)

            if (extracted.contents.length === 0 && extracted.skills.length === 0) {
              extracted = extractProjectItems(aiContext)
            }

            return extracted
          }
        } catch (e) {
        }
      }

      const extracted = extractProjectItems(aiContext)
      return extracted || { contents: [], skills: [] }
    } catch (error) {
      return { contents: [], skills: [] }
    }
  }, [aiContext, realProjectId])

  useEffect(() => {
    if (opened && projectItems) {
      const allIds = new Set<string>()

      const validContents = getValidItems(projectItems.contents)
      const validSkills = getValidItems(projectItems.skills)

      validContents.forEach(item => {
        if (!checkIfSelected(item.id, 'content')) {
          allIds.add(item.id)
        }
      })
      validSkills.forEach(item => {
        if (!checkIfSelected(item.id, 'skill')) {
          allIds.add(item.id)
        }
      })

      setSelectedIds(allIds)
    }
  }, [opened, projectItems, checkIfSelected])

  const handleToggleItem = useCallback((itemId: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }, [])

  const handleSelectAll = useCallback((type: 'contents' | 'skills') => {
    if (!projectItems) return
    
    const items = type === 'contents' ? projectItems.contents : projectItems.skills
    const validItems = getValidItems(items)
    
    if (validItems.length === 0) {
      return
    }
    
    const allSelected = validItems.every(item => selectedIds.has(item.id))
    
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      validItems.forEach(item => {
        if (allSelected) {
          newSet.delete(item.id)
        } else {
          newSet.add(item.id)
        }
      })
      return newSet
    })
  }, [projectItems, selectedIds])

  const handleApplySuggestions = useCallback(async () => {
    if (!realProjectId || !projectItems) {
      notifications.show({
        message: 'ID do projeto não encontrado. Por favor, salve o projeto antes de aplicar sugestões.',
        color: 'red'
      })
      return
    }

    setLoading(true)

    try {
      const contentsToAdd: any[] = []
      const skillsToAdd: any[] = []

      const validContents = getValidItems(projectItems.contents)
      validContents.forEach(item => {
        try {
          if (selectedIds.has(item.id) && !checkIfSelected(item.id, 'content')) {
            contentsToAdd.push({
              ...item,
              type: 'content',
              color: item.color || '#FF9900',
              breadcrumb: item.breadcrumb || '',
              originalCodes: item.originalCodes || '',
              lv1Name: item.lv1Name || '',
              lv1ID: item.lv1Id || '',
              lv2ID: item.lv2Id || '',
              lv2Name: item.lv2Name || '',
              lv3ID: item.lv3Id || '',
              lv3Name: item.lv3Name || '',
              curriculumId: item.curriculumId || '',
              curriculumName: item.curriculumName || '',
              curriculumKind: item.curriculumKind,
              hasAssess: false,
              grades: safeProcessGrades(item.cycles),
              cycles: safeProcessCycles(item.cycles),
              statutory: false,
              descriptors: []
            })
          }
        } catch (itemError) {
        }
      })

      const validSkills = getValidItems(projectItems.skills)
      validSkills.forEach(item => {
        try {
          if (selectedIds.has(item.id) && !checkIfSelected(item.id, 'skill')) {
            skillsToAdd.push({
              ...item,
              type: 'skill',
              color: item.color || '#00A86B',
              breadcrumb: item.breadcrumb || '',
              originalCodes: item.originalCodes || '',
              lv1Name: item.lv1Name || '',
              lv1ID: item.lv1Id || '',
              lv2ID: item.lv2Id || '',
              lv2Name: item.lv2Name || '',
              lv3ID: item.lv3Id || '',
              lv3Name: item.lv3Name || '',
              curriculumId: item.curriculumId || '',
              curriculumName: item.curriculumName || '',
              curriculumKind: item.curriculumKind,
              hasAssess: false,
              grades: safeProcessGrades(item.cycles),
              cycles: safeProcessCycles(item.cycles),
              statutory: false,
              descriptors: []
            })
          }
        } catch (itemError) {
        }
      })

      if (contentsToAdd.length === 0 && skillsToAdd.length === 0) {
        setLoading(false)
        onClose()
        return
      }

      const existingContentsIds = [
        ...selectedStandards.filter(s => s && s.id).map(s => s.id),
        ...aiSuggestedStandards.filter(s => s && s.id).map(s => s.id)
      ]
      const existingSkillsIds = [
        ...selectedSkills.filter(s => s && s.id).map(s => s.id),
        ...aiSuggestedSkills.filter(s => s && s.id).map(s => s.id)
      ]

      const newContentsIds = contentsToAdd.map(c => c.id)
      const newSkillsIds = skillsToAdd.map(s => s.id)

      const result = await applyAndRefetchAIItems({
        projectId: realProjectId,
        contentsIds: newContentsIds,
        skillsIds: newSkillsIds,
        existingContentsIds,
        existingSkillsIds
      })

      if (result.success && result.selectedItems) {
        forceReloadSelectedItems()

        await new Promise(resolve => requestAnimationFrame(() => resolve(undefined)))

        setSkillsStandards(prev => ({
          ...prev,
          selectedItems: {
            contents: result.selectedItems!.contents,
            specificSkills: result.selectedItems!.specificSkills
          }
        }))

        await new Promise(resolve => requestAnimationFrame(() => resolve(undefined)))

        const totalAdded = contentsToAdd.length + skillsToAdd.length
        notifications.show({
          message: `${totalAdded} ${t('new_project_page.items_added_successfully')}`,
          color: 'green',
          icon: React.createElement(IconCheck, { size: 24 })
        })
      } else {
        notifications.show({
          message: result.error || 'Erro ao sincronizar com o servidor',
          color: 'orange'
        })
      }

      setLoading(false)
      onClose()
    } catch (error: any) {
      setLoading(false)
      notifications.show({
        message: error.message || 'Erro ao aplicar sugestões',
        color: 'red'
      })
    }
  }, [
    realProjectId,
    projectItems,
    selectedIds,
    checkIfSelected,
    setSkillsStandards,
    selectedStandards,
    selectedSkills,
    aiSuggestedStandards,
    aiSuggestedSkills,
    forceReloadSelectedItems,
    t,
    onClose
  ])

  const handleSetActiveTab = useCallback((tab: string) => {
    setActiveTab(tab)
  }, [])

  const totalItems = (projectItems?.contents?.length || 0) + (projectItems?.skills?.length || 0)
  const hasItems = totalItems > 0

  const availableContents = getValidItems(projectItems.contents).filter(item => !checkIfSelected(item.id, 'content'))
  const availableSkills = getValidItems(projectItems.skills).filter(item => !checkIfSelected(item.id, 'skill'))
  const availableItems = { contents: availableContents, skills: availableSkills }
  const totalAvailableItems = availableContents.length + availableSkills.length
  const allItemsAlreadyAdded = totalItems > 0 && totalAvailableItems === 0

  return {
    loading,
    projectItems,
    selectedIds,
    activeTab,
    totalItems,
    hasItems,
    availableItems,
    totalAvailableItems,
    allItemsAlreadyAdded,
    handleToggleItem,
    handleSelectAll,
    handleApplySuggestions,
    setActiveTab: handleSetActiveTab
  }
}